<template>
  <div>
    <v-container grid-list-md>
      <!-- HEADER -->
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_my_parks') }}</h1>
          <p>{{ $t('security_my_parks_description') }}</p>
        </v-flex>
      </v-layout>

      <!-- LOADING STATE -->
      <v-layout row wrap v-if="isSecurityLoading || isLoadingAvailableParks">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
        </v-flex>
      </v-layout>

      <!-- ERROR STATE -->
      <v-layout row wrap v-if="getSecurityError || availableParksError">
        <v-flex xs12>
          <v-alert type="error" dismissible>
            {{ getSecurityError || availableParksError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- PARKS LIST (Already Linked) -->
      <v-layout row wrap v-if="myParks.length > 0 && !isSecurityLoading">
        <v-flex xs12>
          <h2>{{ $t('security_my_parks') }} ({{ myParks.length }})</h2>
        </v-flex>
        <v-flex xs12 sm6 md4 v-for="park in myParks" :key="`linked-${park.id}`">
          <v-card class="elevation-3">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ park.park_name }}</h3>
                <small>{{ park.security_company_name }}</small>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('status') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-chip :color="getStatusColor(park.status)" text-color="white" small>
                        {{ park.status_display || park.status }}
                      </v-chip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('park_id') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ park.park }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('created_at') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ formatDate(park.created_at) }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>

              <v-alert v-if="park.status === 'PENDING'" type="warning" class="mt-3">
                {{ $t('security_pending_approval') }}
              </v-alert>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn v-if="park.status === 'ACTIVE'" 
                     color="primary" 
                     flat 
                     @click="goToAssignGuards(park.id)">
                {{ $t('security_assign_guards') }}
              </v-btn>
              <v-btn color="error" flat @click="showUnlinkDialog(park)">
                {{ $t('security_unlink') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- EMPTY STATE - No linked parks -->
      <v-layout row wrap v-if="myParks.length === 0 && !isSecurityLoading">
        <v-flex xs12>
          <v-alert type="info">
            {{ $t('security_no_parks_linked') }}
            <v-btn color="primary" flat @click="showLinkParkModal = true" class="ml-2">
              {{ $t('security_link_park_now') }}
            </v-btn>
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- AVAILABLE PARKS (For Linking) -->
      <v-layout row wrap v-if="availableParks.length > 0 && !isLoadingAvailableParks" class="mt-5">
        <v-flex xs12>
          <h2>{{ $t('security_available_parks') }} ({{ availableParks.length }})</h2>
          <p class="grey--text">{{ $t('security_available_parks_description') }}</p>
        </v-flex>
        <v-flex xs12 sm6 md4 v-for="park in availableParks" :key="`available-${park.id}`">
          <v-card class="elevation-2">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ park.nombre }}</h3>
                <small v-if="park.contract_status">
                  <v-chip :color="getContractStatusColor(park.contract_status)" text-color="white" x-small>
                    {{ getContractStatusLabel(park.contract_status) }}
                  </v-chip>
                </small>
                <small v-else>
                  <v-chip color="grey" text-color="white" x-small>
                    {{ $t('security_new_contract') }}
                  </v-chip>
                </small>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('direccion') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ park.direccion }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile v-if="park.latitude && park.longitude">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('location') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ park.latitude }}, {{ park.longitude }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile v-if="park.created_at">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('requested_at') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ formatDate(park.created_at) }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn v-if="!park.is_already_linked" 
                     color="primary" 
                     flat 
                     @click="sendLinkingRequest(park)"
                     :loading="loadingParkId === park.id">
                {{ $t('security_send_linking_request') }}
              </v-btn>
              <v-btn v-else color="grey" flat disabled>
                {{ park.contract_status === 'PENDING' ? $t('security_contract_pending') : $t('security_contract_active') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- LINK PARK MODAL -->
      <link-park-modal 
        :show="showLinkParkModal"
        @close="showLinkParkModal = false"
        @success="onParkLinked"
      />

      <!-- UNLINK DIALOG -->
      <v-dialog v-model="showUnlinkDialog" max-width="400px">
        <v-card>
          <v-card-title>{{ $t('security_confirm_unlink_park') }}</v-card-title>
          <v-card-text>
            <p v-if="selectedPark">
              {{ $t('confirm_unlink_park_msg') }}
              <strong>{{ selectedPark.park_name }}</strong>?
            </p>
            <v-alert type="warning">
              {{ $t('security_unlink_warning') }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" flat @click="showUnlinkDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="error" flat @click="unlinkPark" :loading="isSecurityLoading">
              {{ $t('security_unlink') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { FETCH_MY_PARKS, UNLINK_PARK } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'
import LinkParkModal from './link_park_modal.vue'
import axios from 'axios'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'MyParks',
  components: {
    LinkParkModal
  },
  data() {
    return {
      showLinkParkModal: false,
      showUnlinkDialog: false,
      selectedPark: null,
      availableParks: [],
      isLoadingAvailableParks: false,
      availableParksError: null,
      loadingParkId: null
    }
  },
  computed: {
    ...mapGetters(['getToken']),
    ...mapGetters('security', ['getMyParks', 'isSecurityLoading', 'getSecurityError']),
    myParks() {
      return this.getMyParks
    }
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        'ACTIVE': 'green',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return colors[status] || 'grey'
    },
    getContractStatusColor(status) {
      const colors = {
        null: 'grey',
        'PENDING': 'warning',
        'ACTIVE': 'success',
        'REJECTED': 'error'
      }
      return colors[status] || 'grey'
    },
    getContractStatusLabel(status) {
      if (status === null) return this.$t('security_new_contract')
      if (status === 'PENDING') return this.$t('security_contract_pending')
      if (status === 'ACTIVE') return this.$t('security_contract_active')
      if (status === 'REJECTED') return this.$t('security_contract_rejected')
      return status
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    showUnlinkDialog(park) {
      this.selectedPark = park
      this.showUnlinkDialog = true
    },
    unlinkPark() {
      this.$store.dispatch(`security/${UNLINK_PARK}`, {
        token: this.getToken,
        parkId: this.selectedPark.park
      })
      .then(() => {
        this.showUnlinkDialog = false
        this.$store.commit('snackbar_change_message', this.$t('security_park_unlinked_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_unlinking_park'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    onParkLinked() {
      this.showLinkParkModal = false
      this.$store.commit('snackbar_change_message', this.$t('security_park_linked_success'))
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_activate_true')
      this.fetchData()
    },
    goToAssignGuards(parkId) {
      this.$router.push(`/security/assign-guards-to-park/${parkId}`)
    },
    fetchData() {
      this.$store.dispatch(`security/${FETCH_MY_PARKS}`, this.getToken)
      this.fetchAvailableParks()
    },
    fetchAvailableParks() {
      this.isLoadingAvailableParks = true
      this.availableParksError = null
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/security/available-parks/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        }
      }
      axios(config)
        .then(resp => {
          console.log('[MyParks] Available parks response:', resp.data)
          this.availableParks = resp.data.parks || []
          this.isLoadingAvailableParks = false
        })
        .catch(err => {
          console.error('[MyParks] Error fetching available parks:', err)
          this.isLoadingAvailableParks = false
          const errorMessage = err.response && err.response.data && err.response.data.detail 
            ? err.response.data.detail
            : this.$t('error_fetching_contracts')
          this.availableParksError = errorMessage
        })
    },
    sendLinkingRequest(park) {
      this.loadingParkId = park.id
      console.log('[MyParks] Sending linking request for park:', park.id)
      
      const config = {
        method: 'post',
        url: `${API_BASE_URL}/security/link-park/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Token ${this.getToken}`
        },
        data: {
          park_id: park.id
        }
      }
      
      axios(config)
        .then(resp => {
          console.log('[MyParks] Linking request successful:', resp.data)
          this.$store.commit('snackbar_change_message', this.$t('security_linking_request_sent'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          // Actualizar lista de parques disponibles para reflejar el cambio
          this.fetchAvailableParks()
        })
        .catch(err => {
          console.error('[MyParks] Error sending linking request:', err)
          let errorMessage = this.$t('error_responding_to_contract')
          
          if (err.response && err.response.data) {
            if (err.response.data.error) {
              errorMessage = err.response.data.error
            } else if (err.response.data.detail) {
              errorMessage = err.response.data.detail
            }
          }
          
          this.$store.commit('snackbar_change_message', errorMessage)
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
        .finally(() => {
          this.loadingParkId = null
        })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    
    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.fetchData()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}
.mt-3 {
  margin-top: 20px;
}
.ml-2 {
  margin-left: 8px;
}
</style>
